import type { TempsDeJAi } from '../api/types';
import { nChoses } from './accords';

/**
 * Les mots de « Maintenant », sur l'ordinateur et au téléphone : ses réglages, ses groupes, le
 * débloquant, et le panneau Réglages du téléphone.
 */

/**
 * « Maintenant » (chantier flottaison, ticket 04) : ses réglages, ses groupes, sa phrase de tête.
 * Jamais le verbe de l'urgence (décision d'Igor du 2026-10-01).
 */
export const REGLAGE_TEMPS = 'J’ai';
export const REGLAGE_LIEU = 'Je suis';
export const REGLAGE_COURAGE = 'Courage';
export const MOTS_LIEU: Record<'chez_moi' | 'dehors', string> = {
  chez_moi: 'Chez moi',
  dehors: 'Dehors',
};
export const MOTS_COURAGE: Record<'d_attaque' | 'normal' | 'fatigue', string> = {
  d_attaque: 'D’attaque',
  normal: 'Normal',
  fatigue: 'Fatigué',
};
/**
 * La flottaison en étages, à l'ordinateur (chantier maintenant-en-etages, modification 01 ;
 * maquette du carnet `maintenant.html`) : les noms de la règle de couleur, à gauche.
 */
export const ETAGE_SURFACE = 'Surface';
export const ETAGE_LEGER = 'Léger';
export const ETAGE_PLUS_LOURD = 'Plus lourd';
export const ETAGE_SOUS_L_EAU = 'Sous l’eau';
export const LA_FLOTTAISON = 'La flottaison';
/**
 * « Maintenant » refait d'après sa maquette (chantier `revue-design`, ticket 01,
 * `design/maintenant.html`) : les réglages repliés, la carte du débloquant, Prioritaire en bloc,
 * « Ma journée » en lien, le sélecteur « Plan de vie ».
 */
/** « J'ai » sans durée choisie, « Je suis » sans lieu : ce que le réglage fermé dit alors. */
export const TOUT_LE_TEMPS = 'Tout le temps';
export const N_IMPORTE_OU = 'N’importe où';
export const TOUS_LES_PROJETS = 'Tous les projets';
export const PLAN_DE_VIE = 'Plan de vie';
export const TOUS = 'Tous';
/** Ce qu'un clic dit d'une chose montrée mais pas encore faite (plans de vie, pièces jointes). */
export const EN_DEVELOPPEMENT = 'En cours de développement, pas encore accessible';
export const DEBLOQUANT = 'Débloquant';
export const SI_TU_N_EN_FAIS_QU_UNE = 'si tu n’en fais qu’une';
export const C_EST_FAIT = 'C’est fait';
export const UNE_AUTRE = 'Une autre';
export const GARDER_POUR_AUJOURDHUI = 'Garder pour aujourd’hui';
export const DANS_MA_JOURNEE = 'Dans ma journée';
export const HEURES_D_OUVERTURE = 'Heures d’ouverture';
export const PRIORITAIRE = 'Prioritaire';
export const AUCUNE_REPONSE_ATTENDUE = 'Tu n’attends aucune réponse.';
export const RIEN_DE_FAISABLE = 'Rien de faisable tout de suite avec ces réglages.';
export const REPLIER = 'Replier';
export const CA_N_A_PAS_MARCHE ='Ça n’a pas marché : rien n’a changé.';
/**
 * La ligne du débloquant quand il ne colle pas aux réglages (chantier `flottaison-suite`, ticket
 * 10) : il reste quels que soient les réglages, et dit en quoi il n'y colle pas. Défaut de
 * Claude, Igor corrige en essayant (2026-10-04).
 */
export const PLUS_LONG_QUE: Record<TempsDeJAi, string> = {
  dix_min: 'Plus long que tes 10 min',
  trente_min: 'Plus long que tes 30 min',
  une_heure: 'Plus long que ton heure',
  demi_journee: 'Plus long que ta demi-journée',
  journee: 'Plus long que ta journée',
};
export const IL_FAUT_SORTIR = 'Il faut sortir de chez toi';
export const DEMANDE_PLUS_DE_COURAGE = 'Demande plus de courage';
/** « Ça libère 3 choses ». */
export function caLibere(combien: number): string {
  return `Ça libère ${nChoses(combien)}`;
}
/** L'annonce d'une tâche cochée depuis « Maintenant ». */
export function faitTitre(titre: string): string {
  return `Fait : « ${titre} ».`;
}
export function gardeePourAujourdhui(titre: string): string {
  return `« ${titre} » est dans ta journée.`;
}
/** « Garder pour aujourd'hui » refusé par le serveur : le bouton se cache, la phrase dit pourquoi. */
export function garderRefuse(refus: string): string {
  return refus === 'ne_tient_pas'
    ? 'Ta journée finirait après minuit : elle n’y tient plus aujourd’hui.'
    : 'Elle ne peut pas entrer dans ta journée aujourd’hui.';
}

/**
 * « Maintenant » au téléphone (chantier `telephone-cadre`, ticket 02, `design/telephone.html`,
 * piste « Maintenant ») : une seule chose en grand, « Ensuite », « Chercher une autre tâche ».
 */
export const A_FAIRE_MAINTENANT = 'À faire maintenant';
export const AUTRE_CHOSE = 'Autre chose';
export const ENSUITE = 'Ensuite';
export const CHERCHER_UNE_AUTRE_TACHE = 'Chercher une autre tâche';
export const FILTRER_PAR_LE_TITRE = 'Filtrer par le titre';
export const VOIR_PLUS = 'Voir plus';
export const PAS_DE_RESEAU = 'Pas de réseau';
export const RIEN_DE_FAISABLE_TOUT_DE_SUITE = 'Rien de faisable tout de suite.';
export const CACHEE_PAR_TES_REGLAGES = 'Cachée par tes réglages';
/** « Débloquant · ça libère 3 choses ». */
export function debloquantQuiLibere(combien: number): string {
  return `${DEBLOQUANT} · ça libère ${nChoses(combien)}`;
}
/** « 12 cachées par tes réglages », « 1 cachée par tes réglages ». */
export function cacheesParTesReglages(combien: number): string {
  return `${combien} ${combien > 1 ? 'cachées' : 'cachée'} par tes réglages`;
}
/**
 * Le panneau Réglages de « Maintenant » au téléphone (chantier `telephone-cadre`, ticket 03,
 * `design/telephone.html`, piste « Maintenant ») : un bouton, un panneau, « Voir les 3 possibles ».
 */
export const REGLAGES = 'Réglages';
export const REGLAGE_PROJET = 'Projet';
export const REVENIR_A_TOUT = 'Revenir à tout';
/**
 * Les pièces du carnet sur « Maintenant » au téléphone (chantier telephone-habit, 04, maquette
 * `maintenant()`) : « Ensuite » et sa petite phrase, les gestes du post-it d'« À vérifier ».
 */
export const PENDANT_CE_TEMPS_DE_ENSUITE = 'Pendant ce temps';
export const C_EST_ARRIVE = 'C’est arrivé';
export const RELANCER = 'Relancer';
/** « Voir les 3 possibles » ; `null` : le compte n'est pas encore arrivé. */
export function voirLesPossibles(combien: number | null): string {
  if (combien === null) return 'Voir';
  if (combien === 0) return 'Voir : aucune possible';
  if (combien === 1) return 'Voir la seule possible';
  return `Voir les ${combien} possibles`;
}
/** « Le débloquant de ce projet prend 1 h : « M'inscrire à France Travail ». » */
export function debloquantTropLong(titre: string, duree: string, deCeProjet: boolean): string {
  return `Le débloquant${deCeProjet ? ' de ce projet' : ''} ${duree} : « ${titre} ».`;
}
/** « Aucune tâche ne contient « banque ». » */
export function aucuneTacheNeContient(texte: string): string {
  return `Aucune tâche ne contient « ${texte} ».`;
}
